import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { AnimatedSprite, Geometry, Shader, Buffer, State, DRAW_MODES, Program } from "pixi.js";

/* babel-plugin-inline-import '../shader/waterOverlay.vert' */
const waterOverlayVertexCode = "precision highp float;\n\nattribute vec2 aVertexPosition;\nattribute vec2 aTextureCoord;\nattribute vec2 aTextureCoordWaterMask;\n\nuniform mat3 projectionMatrix;\n\nvarying vec2 vVertexPosition;\nvarying vec2 vTextureCoord;\nvarying vec2 vTextureCoordWaterMask;\n\nvoid main(void) {\n   gl_Position = vec4((projectionMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);\n\n   vVertexPosition = aVertexPosition;\n   vTextureCoord = aTextureCoord;\n   vTextureCoordWaterMask = aTextureCoordWaterMask;\n}\n";

/* babel-plugin-inline-import '../shader/waterOverlay.frag' */
const waterOverlayFragmentCode = "precision highp float;\n\nvarying vec2 vVertexPosition;\nvarying vec2 vTextureCoord;\nvarying vec2 vTextureCoordWaterMask;\nuniform sampler2D uSampler;\n\nuniform sampler2D uSamplerWaterMask;\n\nuniform mat3 cameraTranslationMatrix;\nuniform float height;\nuniform float maxV;\n\nuniform float timeS;\n\n#define SHALLOW_WATER 1\n\n//[OVERWRITE]\nvoid calculateWaves(mat3 matrix, vec2 position, out float scale, out vec2 wavesUv, out float wavesDisplacement, out vec4 wavesColor) { }\n//[/OVERWRITE]\n\nfloat easeDepth(float x) {\n   return x * x * x;\n}\n\nvoid main(void) {\n   float wavesDisplacement;\n   float scale;\n   vec2 wavesUv;\n   vec4 wavesColor;\n   calculateWaves(cameraTranslationMatrix, vVertexPosition, scale, wavesUv, wavesDisplacement, wavesColor);\n\n   wavesDisplacement += cos(wavesUv.x * 0.1 + timeS * 1.37) * 2.0 * scale;\n\n   vec2 wavyTextureCoord = vTextureCoordWaterMask;\n   wavyTextureCoord.y += wavesDisplacement / height;\n   wavyTextureCoord.y = min(wavyTextureCoord.y, maxV);\n\n   vec4 backgroundImageUndistorted = texture2D(uSampler, vTextureCoord);\n   vec4 waterMaskUndistorted = texture2D(uSamplerWaterMask, vTextureCoordWaterMask);\n   vec4 waterMaskDistorted = texture2D(uSamplerWaterMask, wavyTextureCoord);\n\n   float fadedOutDepthColor = 0.0;\n   float depth = waterMaskUndistorted.a * (1.0 - clamp((waterMaskUndistorted.g - fadedOutDepthColor) / (1.0 - fadedOutDepthColor), 0.0, 1.0));\n   float fade = mix(0.7, 1.0, easeDepth(depth));\n   //fade = 0.7;\n\n   float waterMaskDistortedGRaw = (waterMaskDistorted.g / waterMaskDistorted.a);\n   float border = 0.02;\n   float thickness = 0.07;\n   float borderMin = 1.0 - thickness - border * 2.0;\n   float whiteLineValueBorderUp = 1.0 - clamp((waterMaskDistortedGRaw - (1.0 - border)) / border, 0.0, 1.0);\n   float whiteLineValueBorderDown = clamp((waterMaskDistortedGRaw - borderMin) / border, 0.0, 1.0);\n   float whiteLine = whiteLineValueBorderUp * whiteLineValueBorderDown * backgroundImageUndistorted.a * 0.75;\n\n   float alpha = min(ceil(backgroundImageUndistorted.a), floor(waterMaskDistorted.a)) * fade * whiteLineValueBorderUp;\n\n   gl_FragColor = mix(alpha * wavesColor, vec4(119.0 / 255.0, 163.0 / 255.0, 148.0 / 255.0, 1.0), whiteLine);\n}\n";

/* babel-plugin-inline-import '../shader/water.frag' */
const waterFragmentCode = "precision highp float;\n\nuniform float timeS;\n\n//[SHARED]\nuniform sampler2D uSamplerWaves;\nuniform float waveTextureScale;\n\nvec4 colorLayer1 = vec4(88.0 / 255.0, 137.0 / 255.0, 126.0 / 255.0, 1.0);\nvec4 colorLayer2 = vec4(21.0 / 255.0, 51.0 / 255.0, 54.0 / 255.0, 1.0);\nvec4 colorLayer3 = vec4(33.0 / 255.0, 69.0 / 255.0, 68.0 / 255.0, 1.0);\nvec4 colorBackground = vec4(45.0 / 255.0, 87.0 / 255.0, 84.0 / 255.0, 1.0);\n\nvoid calculateWaves(mat3 matrix, vec2 position, out float scale, out vec2 wavesUv, out float wavesDisplacement, out vec4 wavesColor) {\n   vec2 offset = vec2(matrix[2][0], matrix[2][1]);\n   scale = matrix[0][0];\n\n   wavesUv = (position - offset) / scale;\n\n   wavesDisplacement = cos(wavesUv.x * 0.01 + timeS * 1.0) * 10.0 * scale;\n\n   wavesColor = colorBackground;\n\n#ifndef SHALLOW_WATER\n   float layer3 = texture2D(uSamplerWaves, fract(wavesUv * waveTextureScale)).b;\n   wavesColor = mix(wavesColor, colorLayer3, layer3 * 0.5);\n\n   float layer2 = texture2D(uSamplerWaves, fract((wavesUv + wavesDisplacement / 3.0) * waveTextureScale)).g;\n   wavesColor = mix(wavesColor, colorLayer2, layer2 * 0.35);\n#endif\n\n   float layer1 = texture2D(uSamplerWaves, fract((wavesUv + wavesDisplacement) * waveTextureScale)).r;\n   wavesColor = mix(wavesColor, colorLayer1, layer1 * 0.4);\n}\n//[/SHARED]\n\nvarying vec2 vTextureCoord;\n\nuniform mat3 translationMatrix;\n\nvoid main() {\n   float scale;\n   vec2 wavesUv;\n   float displacement;\n   vec4 wavesColor;\n   calculateWaves(translationMatrix, vTextureCoord, scale, wavesUv, displacement, wavesColor);\n\n   gl_FragColor = wavesColor;\n   //gl_FragColor = vec4(wavesUv.x, 0, wavesUv.y, 1.0);\n}\n";
import { typedArrayValuesEqual } from "../../../helper/pixiHelpers";

const keywordRegex = keyword => new RegExp(`\\/\\/\\[${keyword}\\](.*)//\\[\\/${keyword}\\]`, "s"); // matches [keyword] ... [/keyword]


const sharedWaterFragmentCode = keywordRegex("SHARED").exec(waterFragmentCode)[1];
const processedWaterOverlayFragmentCode = waterOverlayFragmentCode.replace(keywordRegex("OVERWRITE"), sharedWaterFragmentCode);
let shaderProgram;
export class WaterOverlayAnimatedSprite extends AnimatedSprite {
  constructor(water, textures, autoUpdate) {
    super(textures, autoUpdate);

    _defineProperty(this, "water", void 0);

    _defineProperty(this, "scrollTransform", void 0);

    _defineProperty(this, "uniforms", void 0);

    _defineProperty(this, "shader", void 0);

    _defineProperty(this, "vertexPositionBuffer", void 0);

    _defineProperty(this, "textureCoordBuffer", void 0);

    _defineProperty(this, "textureCoordWaterMaskBuffer", void 0);

    _defineProperty(this, "geometry", void 0);

    _defineProperty(this, "previousVertexData", void 0);

    _defineProperty(this, "previousUVs", void 0);

    _defineProperty(this, "waterMaskTextureID", void 0);

    _defineProperty(this, "state", State.for2d());

    _defineProperty(this, "drawMode", DRAW_MODES.TRIANGLES);

    _defineProperty(this, "start", 0);

    _defineProperty(this, "size", 0);

    this.water = water;
    this.scrollTransform = water.transform;
    const waterUniforms = water.uniforms;
    this.uniforms = {
      uSamplerWaves: waterUniforms.uSamplerWaves,
      waveTextureScale: waterUniforms.waveTextureScale,
      timeS: 0,
      uSampler: this.texture,
      uSamplerWaterMask: null,
      height: 0,
      maxV: 0,
      cameraTranslationMatrix: null
    };

    if (!shaderProgram) {
      shaderProgram = new Program(waterOverlayVertexCode, processedWaterOverlayFragmentCode, "WaterOverlay");
    }

    this.shader = new Shader(shaderProgram, this.uniforms);
    this.vertexPositionBuffer = new Buffer(this.vertexData);
    this.previousVertexData = new Float32Array(this.vertexData);
    this.textureCoordBuffer = new Buffer(this.uvs);
    this.previousUVs = new Float32Array(this.uvs);
    this.textureCoordWaterMaskBuffer = new Buffer();
    const indexBuffer = new Buffer(this.indices, false, true);
    this.geometry = new Geometry().addAttribute("aVertexPosition", this.vertexPositionBuffer, 2).addAttribute("aTextureCoord", this.textureCoordBuffer, 2).addAttribute("aTextureCoordWaterMask", this.textureCoordWaterMaskBuffer, 2).addIndex(indexBuffer);
    this.state = State.for2d();
  }

  set waterMaskTexture(value) {
    this.uniforms.uSamplerWaterMask = value;
  }

  get waterMaskTexture() {
    return this.uniforms.uSamplerWaterMask;
  }

  _render(renderer) {
    if (!this.waterMaskTexture || !this.texture) return;
    this.calculateVertices(); // Update vertex position buffer if vertex positions changed

    if (!typedArrayValuesEqual(this.previousVertexData, this.vertexData)) {
      this.previousVertexData.set(this.vertexData);
      this.vertexPositionBuffer.update(this.vertexData);
    } // Update texture coordinate buffer if UVs changed


    if (!typedArrayValuesEqual(this.previousUVs, this.uvs)) {
      this.previousUVs.set(this.uvs);
      this.textureCoordBuffer.update(this.uvs);
    }

    if (this.waterMaskTextureID !== this.waterMaskTexture._updateID) {
      this.waterMaskTextureID = this.waterMaskTexture._updateID;
      this.textureCoordWaterMaskBuffer.update(this.waterMaskTexture._uvs.uvsFloat32);
    }

    this.shader.uniforms.uSampler = this.texture;
    this.uniforms.timeS = this.water.uniforms.timeS;
    this.uniforms.cameraTranslationMatrix = this.scrollTransform.worldTransform.toArray(true); // Set the vertex height scaled by the UV height for wave scaling

    this.uniforms.height = (this.vertexData[5] - this.vertexData[1]) / (this.waterMaskTexture._uvs.uvsFloat32[5] - this.waterMaskTexture._uvs.uvsFloat32[1]); // Set the maximum V coordinate to the lower part of the UV rect minus 1 pixel (to stay in the black part even in spritesheets)

    this.uniforms.maxV = this.waterMaskTexture._uvs.uvsFloat32[5] - 1 / this.waterMaskTexture.baseTexture.height; // From here on this is a 1:1 copy of PIXI.Mesh._renderDefault()

    const shader = this.shader;
    shader.alpha = this.worldAlpha;

    if (shader.update) {
      shader.update();
    }

    renderer.batch.flush();
    shader.uniforms.translationMatrix = this.transform.worldTransform.toArray(true); // bind and sync uniforms..

    renderer.shader.bind(shader); // set state..

    renderer.state.set(this.state); // bind the geometry...

    renderer.geometry.bind(this.geometry, shader); // then render it

    renderer.geometry.draw(this.drawMode, this.size, this.start, this.geometry.instanceCount);
  }

  destroy(options) {
    this.geometry.destroy();
    super.destroy(options);
    this.shader.destroy();
    this.vertexPositionBuffer.destroy();
    this.textureCoordBuffer.destroy();
    this.textureCoordWaterMaskBuffer.destroy();
  }

}